import type { ActionEnum, MultiPortfolioPersistResult, Portfolio, PrivatePortfolioNode } from 'venn-api';
import type { ReactNode } from 'react';
import type { FeatureName } from 'venn-utils';
import type { HistoricalFundRangeAnalysis } from './views/components/specialized/MultiHistoricalPortfolioContext';
import {
  IAssignOwnershipView,
  IChooseActionPortfolioView,
  IChooseActionView,
  ICreateMasterPortfolioView,
  ICreatePrivatePortfolioView,
  IDuplicatePortfolioView,
  INewPortfolioView,
  IPortfolioUploadConfirmationView,
  IReviewHistoricalPortfolioView,
  IReviewPrivatesView,
  IReviewProFormaPortfolioView,
  IReviewReturnsView,
  IUploadPortfolioView,
  IUploadPrivatesView,
  IUploadReturnsView,
} from './constants';

export const APPEND_ONLY = 'Append Only';
export const DO_NOT_MAP = 'Do Not Map';
export const FUND = 'Fund';

export enum AppendType {
  OVERWRITE_ALL = 1,
  OVERWRITE_OVERLAPPING,
  APPEND,
}

export enum DataUploaderMode {
  Returns,
  NewNavs,
  Initial,
  Privates,
  Portfolios,
}

// assign string values just so that number values won't incorrectly be used as the step number
export enum DataUploaderView {
  Upload = 'Upload',
  Review = 'Review',
  DisplayNavMapping = 'DisplayNavMapping',
  Initial = 'Initial',
}

export interface DataCell {
  // 0-indexed
  row?: number;
  column: number;
  value: string;
}

export interface DataPoint {
  x: number;
  y: number;
}

export const DO_NOT_MAP_ID = 1;

export type PortfolioMultiUploaderProps = {
  closeUploaderSlideout: () => void;
  lastTimeBeforeUpdate?: number;
  onPrivatePortfolioCreated: (portfolio: PrivatePortfolioNode) => void;
};

export type MultiUploaderReviewViewId = 'REVIEW_RETURNS' | 'REVIEW_PRIVATES';
export type MultiUploaderUploadViewId = 'UPLOAD_RETURNS' | 'UPLOAD_PRIVATES';
type MultiUploaderChooseActionView = 'CHOOSE_ACTION' | 'CHOOSE_ACTION_PORTFOLIO';

export type MultiPortfolioUploaderViewId =
  | 'UPLOAD_PORTFOLIO'
  | 'REVIEW_PRO_FORMA_PORTFOLIO'
  | 'PORTFOLIO_UPLOAD_CONFIRMATION'
  | 'REVIEW_HISTORICAL_PORTFOLIO'
  | 'ASSIGN_OWNERSHIP';

type PortfolioMultiUploaderViewId =
  | MultiUploaderReviewViewId
  | MultiUploaderUploadViewId
  | MultiUploaderChooseActionView
  | MultiPortfolioUploaderViewId
  | 'DUPLICATE_PORTFOLIO'
  | 'NEW_PORTFOLIO'
  | 'CREATE_PRIVATE_PORTFOLIO'
  | 'CREATE_MASTER_PORTFOLIO';

const PortfolioMultiUploaderView = [
  IChooseActionView,
  IUploadReturnsView,
  IReviewReturnsView,
  IUploadPrivatesView,
  IReviewPrivatesView,
  ICreateMasterPortfolioView,
  IChooseActionPortfolioView,
  IUploadPortfolioView,
  IReviewProFormaPortfolioView,
  IReviewHistoricalPortfolioView,
  IAssignOwnershipView,
  IPortfolioUploadConfirmationView,
  IDuplicatePortfolioView,
  INewPortfolioView,
  ICreatePrivatePortfolioView,
];
export type PortfolioMultiUploaderView = (typeof PortfolioMultiUploaderView)[number];
export type PortfolioMultiUploaderViewTemplate = {
  requiredPermissions: ActionEnum[];
  requiredFeatures: FeatureName[];
  id: PortfolioMultiUploaderViewId;
  title: (hasHistorical: boolean) => string;
  shortTitle?: (hasHistorical: boolean) => string | undefined;
  subtitle?: ReactNode | (() => ReactNode);
  description?: (hasHistorical: boolean) => string;
  highlightText?: (hasTaxImpact: boolean) => string | undefined;
  tileHeader?: string;
};

export type PortfolioAccessMode = 'CREATE_NEW' | 'OVERWRITE_EXISTING';

export type UploadPortfolioAllocatorProps = {
  portfolio: Portfolio;
  selectedIndex: number;
  updatePortfolio: (index: number, portfolio: Portfolio) => void;
  accessMode: PortfolioAccessMode;
};

export type RowData<T> = {
  path: number[];
  node: T;
  root: T;
  isHidden?: boolean;
  isFillForward?: boolean;
  isLotRow?: boolean;
};

export type MultiPortfolioUploadConfirmationData = Readonly<{
  persistResult: MultiPortfolioPersistResult;
  rangeAnalysis?: HistoricalFundRangeAnalysis;
}>;
